import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { Loader, SdirModal } from '@sdir/sds';
import {
  AttachmentType,
  GetApplicationsQueryResult,
} from '@sdir/httpclient/lib/clients/aps/application';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import Overrule from '../Overrule/Overrule';
import { useAttachmentGroupWorkspace, useSubmitSequence } from '../../../../services/hooks';
import { overruleApi } from '../../../../httpclient';
import {
  CreateAttachmentGroupTracker,
  SubmitAttachmentUpdatesParams,
  SubmitAttachmentUpdatesTracker,
  buildCreateAttachmentGroupSequence,
  buildSubmitAttachmentUpdatesSequence,
} from '../../../../helpers/attachmentApiHelpers';

interface OverruleModalProps {
  application?: GetApplicationsQueryResult;
  show: boolean;
  onClose?: () => void;
}

const OverruleModal: React.FC<OverruleModalProps> = ({ application, show = false, onClose }) => {
  const intl = useIntl();
  const [overruleComment, setOverruleComment] = useState('');
  const [overruleChecked, setOverruleChecked] = useState(false);
  const [showOverruleValidation, setShowOverruleValidation] = useState(false);
  const {
    loading: overruleLoading,
    errorObject: overruleError,
    callApi: saveOverrule,
  } = useApi({
    apiFunction: (payload) => overruleApi.v1OverruleAttachCommandPut(payload),
    callApiOnInit: false,
  });

  const { attachmentGroupOverrule, attachmentsOverrule } = useMemo(() => {
    const attachments = application?.attachments || [];
    const overruleAttachments = attachments.filter((a) => a.type === AttachmentType.OverRule);
    return {
      attachmentGroupOverrule: overruleAttachments?.[0]?.group || null,
      attachmentsOverrule: overruleAttachments,
    };
  }, [application?.attachments]);

  const { availableFiles, changes, handleAddNewFiles, handleRemoveActiveFile } =
    useAttachmentGroupWorkspace(attachmentGroupOverrule || 'new');

  const [submitAttachmentGroupTracker, setSubmitAttachmentGroupTracker] =
    useState<CreateAttachmentGroupTracker>({});
  const [submitAttachmentsTracker, setSubmitAttachmentsTracker] =
    useState<SubmitAttachmentUpdatesTracker>({});
  /* eslint-disable @typescript-eslint/indent */
  const { submit: submitAttachments } = useSubmitSequence<
    SubmitAttachmentUpdatesParams,
    SubmitAttachmentUpdatesTracker
  >(buildSubmitAttachmentUpdatesSequence);
  /* eslint-enable @typescript-eslint/indent */
  const { submit: submitAttachmentGroup } = useSubmitSequence(buildCreateAttachmentGroupSequence);

  useEffect(() => {
    setOverruleChecked(!!application?.userComment);
    setOverruleComment(application?.userComment || '');
  }, [application?.userComment]);

  const submitOverrule = async () => {
    try {
      if (!application) return;

      if (overruleChecked) {
        if (overruleComment?.length > 0) {
          setShowOverruleValidation(false);
          const res = await saveOverrule({
            applicationUid: application?.uid,
            comment: overruleComment,
          });
          if (!res?.success) throw Promise.reject(res);
          const { newFiles, removedAttachments, updatedAttachments } = changes;
          if (!attachmentGroupOverrule) {
            // Create new attachment group
            await submitAttachmentGroup(
              {
                application,
                attachmentType: AttachmentType.OverRule,
                files: newFiles,
                simulatorResultItemUri: null,
              },
              submitAttachmentGroupTracker
            );
          } else {
            // Submit attachments, when we already have an attachment group
            const fileUpdateParams = {
              application,
              attachmentGroup: attachmentGroupOverrule,
              qualificationUri: null,
              updatedAttachments,
              removedAttachments,
              newAttachments: newFiles.map((x) => ({
                file: x,
                type: AttachmentType.OverRule,
              })),
            };
            await submitAttachments(fileUpdateParams, submitAttachmentsTracker);
          }
          onClose?.();
        } else {
          setShowOverruleValidation(true);
        }
      } else {
        // Cleanup potential overrule saved from before
        if (application?.userComment) {
          const res = await saveOverrule({
            applicationUid: application?.uid,
            comment: '',
          });
          if (!res?.success) throw Promise.reject(res);
        }
        if (attachmentGroupOverrule) {
          // Cleanup/remove all attachments of type OverRule
          const removedAttachments = attachmentsOverrule?.map((a) => a?.uid || '');
          const fileUpdateParams = {
            application,
            attachmentGroup: attachmentGroupOverrule,
            qualificationUri: null,
            updatedAttachments: [],
            removedAttachments: removedAttachments || [],
            newAttachments: [],
          };
          await submitAttachments(fileUpdateParams, submitAttachmentsTracker);
        }
        onClose?.();
      }
    } catch (error) {
      console.log('error: ', error);
      throw new Error();
    }
  };

  return (
    <StyledModal
      width="600px"
      visible={show}
      onCancel={onClose}
      onOK={submitOverrule}
      okText={overruleLoading ? undefined : 'Lagre'}
    >
      <Wrapper>
        <Overrule
          availableFiles={availableFiles}
          checked={overruleChecked}
          checkboxLabel={intl.formatMessage({ id: 'summarystep.expirydate.overrule.checklabel' })}
          comment={overruleComment}
          error={overruleError}
          hideHeading
          showValidation={showOverruleValidation}
          onNewFilesAdded={handleAddNewFiles}
          onDeleteFile={handleRemoveActiveFile}
          setOverruleChecked={setOverruleChecked}
          setOverruleComment={setOverruleComment}
          setShowOverruleValidation={setShowOverruleValidation}
        />
        {overruleLoading && <Loader />}
      </Wrapper>
    </StyledModal>
  );
};

export default OverruleModal;

const StyledModal = styled(SdirModal)`
  font-size: 16px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0px 0;
`;
